/**
 * Where a traffic truck's trailer goes: along the path its kingpin drove.
 *
 * A trailer pulled freely (a tractrix) cuts inside every bend: on a
 * roundabout's ring about two metres, over the kerb onto the island. A real
 * driver swings the tractor wide so the trailer's wheels stay on the road.
 * Traffic drives the tractor down the middle of its lane, so instead the
 * trailer's axles are put on the kingpin's own track: they follow the lane,
 * the trailer's body spanning it as a chord. That holds through any turn,
 * lane change or snap from one lane to the next. The track is a short
 * breadcrumb trail, oldest point first, trimmed to what the trailer reaches.
 */

/** How far apart the trail's points are laid, m. */
const STEP = 0.4;

/**
 * Lay the kingpin at (kx, kz) on `trail` (x, z pairs, oldest first) and
 * return where the axles are, `reach` metres back along it, straight-line.
 * Points beyond what the axles need are dropped. With too short a trail, the
 * axles are straight back past its oldest point, or along (backX, backZ)
 * when there is no trail at all.
 */
export function followTrail(trail: number[], kx: number, kz: number, reach: number, backX: number, backZ: number): { x: number; z: number } {
  // The newest point is the kingpin itself; a new one is laid each step it goes.
  const n = trail.length;
  if (n < 4 || Math.hypot(trail[n - 2] - trail[n - 4], trail[n - 1] - trail[n - 3]) >= STEP) trail.push(kx, kz);
  else { trail[n - 2] = kx; trail[n - 1] = kz; }
  // Back from the newest, for the first point at least `reach` from the kingpin.
  for (let i = trail.length - 4; i >= 0; i -= 2) {
    const bx = trail[i], bz = trail[i + 1];
    if (Math.hypot(bx - kx, bz - kz) < reach) continue;
    // On the segment from the newer point A to B: |A + t(B - A) - K| = reach.
    const ax = trail[i + 2], az = trail[i + 3];
    const ex = bx - ax, ez = bz - az, fx = ax - kx, fz = az - kz;
    const a = ex * ex + ez * ez, b = 2 * (ex * fx + ez * fz), c = fx * fx + fz * fz - reach * reach;
    const t = a > 1e-9 ? Math.max(0, Math.min(1, (-b + Math.sqrt(Math.max(0, b * b - 4 * a * c))) / (2 * a))) : 1;
    if (i > 0) trail.splice(0, i);
    return { x: ax + ex * t, z: az + ez * t };
  }
  // Not yet a trailer's length of it: straight on back from the kingpin.
  let dx = trail[0] - kx, dz = trail[1] - kz;
  const d = Math.hypot(dx, dz);
  if (d < 0.5) { dx = backX; dz = backZ; } else { dx /= d; dz /= d; }
  return { x: kx + dx * reach, z: kz + dz * reach };
}

/** Move the whole trail by (dx, dz): the world's origin moved, or the truck was put somewhere new. */
export function shiftTrail(trail: number[], dx: number, dz: number): void {
  for (let i = 0; i < trail.length; i += 2) { trail[i] += dx; trail[i + 1] += dz; }
}
